import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UploadedFile } from '../components/FileUploader';
import { PolicyOption } from '../components/PolicyComparison';
import { ChatMessage } from '../components/ChatInterfaceQuoteUpdate';
import { useAuth } from './AuthContext';

export interface PolicyDocument extends UploadedFile {
  extractedText?: string;
  insights?: string[];
  recommendations?: string[];
  riskScore?: number;
  policyOption?: PolicyOption;
  summary?: string;
  keyTerms?: string[];
  policyType?: string;
  fileUrl?: string;
  projectId?: string;
  customerId?: string;
}

interface DocumentContextType {
  documents: PolicyDocument[];
  createChatSession: (customerId: string | null, projectId: string | null, title?: string) => Promise<string | null>;
  setDocuments: React.Dispatch<React.SetStateAction<PolicyDocument[]>>;
  addDocuments: (newFiles: UploadedFile[]) => void;
  removeDocument: (docId: string) => void;
  updateDocument: (docId: string, updates: Partial<PolicyDocument>) => void;
  getCompletedDocuments: () => PolicyDocument[];
  getPolicyOptions: () => PolicyOption[];
  selectedPolicyType: string;
  setSelectedPolicyType: (type: string) => void;
  chatHistory: ChatMessage[];
  setChatHistory: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  addChatMessage: (message: ChatMessage, title?: string, explicitSessionId?: string) => Promise<string | null>;
  clearChatHistory: () => Promise<void>;
  exportChatHistory: () => void;
  startNewSession: () => void;
  setCurrentCustomer: (customerId: string | null) => void;
  setCurrentProject: (projectId: string | null) => void;
  setCurrentChatSession: (sessionId: string | null) => void;
  selectedDocumentIds: string[];
  setSelectedDocumentIds: React.Dispatch<React.SetStateAction<string[]>>;
  toggleDocumentSelection: (docId: string) => void;
  selectedDocuments: PolicyDocument[];
  clearDocuments: () => void;
  currentCustomerId: string | null;
  currentProjectId: string | null;
  currentChatSessionId: string | null;
  refreshDocuments: () => void;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export const useDocuments = () => {
  const context = useContext(DocumentContext);
  if (context === undefined) {
    throw new Error('useDocuments must be used within a DocumentProvider');
  }
  return context;
};

interface DocumentProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export function DocumentProvider({ children }: DocumentProviderProps): JSX.Element {
  // Track currently selected customer, project, and chat session purely in React state
  const [currentCustomerId, setCurrentCustomerId] = useState<string | null>(null);
  const [currentProjectId, _setCurrentProjectId] = useState<string | null>(null);
  const [currentChatSessionId, setCurrentChatSessionId] = useState<string | null>(null);

  // Wrapper to track all changes to currentProjectId
  const setCurrentProjectId = (projectId: string | null) => {
    console.log('🆔 currentProjectId changing from', currentProjectId, 'to', projectId);
    console.log('🆔 Stack trace:', new Error().stack);
    _setCurrentProjectId(projectId);
  };
  const { token, user } = useAuth();
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  
  const storageKeySel = `riskninja-selected-documents-${user?.id}`;
  const [selectedPolicyType, setSelectedPolicyType] = useState<string>('general-liability');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>(() => {
    if (user) {
      const stored = localStorage.getItem(storageKeySel);
      if (stored) {
        try { return JSON.parse(stored) as string[]; } catch {} 
      }
    }
    return [];
  });
  const toggleDocumentSelection = (docId: string) => {
    setSelectedDocumentIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };
  const selectedDocuments = Array.isArray(documents) ? documents.filter(doc => selectedDocumentIds.includes(doc.id)) : [];
  const clearDocuments = () => {
    setSelectedDocumentIds([]);
  };

  useEffect(() => {
    setChatHistory([]);
  }, []);

  const refreshDocuments = () => {
    if (!token) {
      console.log('🚫 RefreshDocuments: No token');
      setDocuments([]);
      return;
    }
    
    if (!currentProjectId) {
      console.log('🚫 RefreshDocuments: No projectId - clearing documents for project isolation');
      setDocuments([]);
      return;
    }
    
    const url = `${API_BASE_URL}/api/documents?projectId=${currentProjectId}`;
    console.log('📄 RefreshDocuments: Fetching from', url, 'at', new Date().toISOString());
    
    // Store the projectId at the time of the request to detect race conditions
    const requestProjectId = currentProjectId;
    
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return res.json();
      })
      .then((docs: PolicyDocument[] | any) => {
        // Check if the project changed while we were fetching
        if (currentProjectId !== requestProjectId) {
          console.log('⚠️ RefreshDocuments: Project changed during fetch! Request was for', requestProjectId, 'but current is', currentProjectId, '- ignoring results');
          return;
        }
        
        const docsArray = Array.isArray(docs) ? docs : [];
        
        // STRICT PROJECT ISOLATION: Verify all documents belong to the current project
        const invalidDocs = docsArray.filter(doc => doc.projectId !== currentProjectId);
        if (invalidDocs.length > 0) {
          console.error('🚨 RefreshDocuments: Received documents that do not belong to current project!');
          console.error('   Current project:', currentProjectId);
          console.error('   Invalid documents:', invalidDocs.map(d => ({ id: d.id, name: d.name, projectId: d.projectId })));
          // Only keep documents that strictly match the current project
          const validDocs = docsArray.filter(doc => doc.projectId === currentProjectId);
          setDocuments(validDocs);
        } else {
          setDocuments(docsArray);
        }
        
        console.log('📄 RefreshDocuments: Received', docsArray.length, 'documents for project', currentProjectId, 'at', new Date().toISOString());
        console.log('📄 Documents:', docsArray.map(d => ({ id: d.id, name: d.name, projectId: d.projectId })));
        
        // Debug: Check if extractedText is present
        console.log('📄 RefreshDocuments: Documents with extractedText:', docsArray.map(d => ({
          id: d.id,
          name: d.name,
          hasExtractedText: !!d.extractedText,
          extractedTextLength: d.extractedText ? d.extractedText.length : 0
        })));
        
        if (user) {
          const stored = localStorage.getItem(storageKeySel);
          if (stored === null) {
            setSelectedDocumentIds(docsArray.map(d => d.id));
          }
        }
      })
      .catch(err => {
        console.error('❌ RefreshDocuments error:', err);
        // On error, clear documents to prevent showing incorrect data
        setDocuments([]);
      });
  };

  useEffect(() => {
    console.log('🔄 useEffect triggered - refreshing documents for project:', currentProjectId);
    console.log('🔄 Dependencies:', { token: !!token, user: !!user, storageKeySel, currentProjectId });
    // Only refresh if we have a valid project
    if (currentProjectId && token) {
      refreshDocuments();
    } else {
      console.log('🔄 Skipping refresh - no project or token');
    }
  }, [token, user, currentProjectId]);

  useEffect(() => {
    if (user) {
      localStorage.setItem(storageKeySel, JSON.stringify(selectedDocumentIds));
    }
  }, [selectedDocumentIds, storageKeySel]);

  const addDocuments = async (newFiles: UploadedFile[]) => {
    if (!token) {
      console.error('Cannot upload documents: no authentication token');
      alert('Authentication required. Please log in again.');
      return;
    }
    
    if (!currentProjectId) {
      console.error('Cannot upload documents: no project selected');
      alert('Please select a project before uploading documents.');
      return;
    }
    
    console.log('📤 AddDocuments: Uploading', newFiles.length, 'files to project', currentProjectId);
    
    for (const file of newFiles) {
      let fileName = file.name;
      let skipFile = false;
      while (true) {
        const form = new FormData();
        form.append('file', file.file!);
        form.append('name', fileName);
        form.append('projectId', currentProjectId);
        if (currentCustomerId) {
          form.append('customerId', currentCustomerId);
        }
        try {
          const res = await fetch(`${API_BASE_URL}/api/documents`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: form
          });
          if (res.ok) {
            const created: PolicyDocument = await res.json();
            
            // STRICT PROJECT ISOLATION: Verify the created document belongs to current project
            if (created.projectId !== currentProjectId) {
              console.error('🚨 AddDocuments: Created document does not belong to current project!');
              console.error('   Expected project:', currentProjectId);
              console.error('   Document project:', created.projectId);
              alert('Error: Document was not properly associated with the current project. Please try again.');
              break;
            }
            
            setDocuments(prev => [...prev, created]);
            setSelectedDocumentIds(prev => [...prev, created.id]);
            console.log('✅ AddDocuments: Successfully uploaded', fileName, 'to project', currentProjectId);
            break;
          } else if (res.status === 409) {
            const newName = window.prompt(
              `A document named "${fileName}" already exists. Please enter a new name:`,
              fileName
            )?.trim();
            if (!newName) {
              skipFile = true;
              break;
            }
            fileName = newName;
            continue;
          } else {
            console.error('Failed to upload document:', await res.text());
            break;
          }
        } catch (err) {
          console.error('Upload error:', err);
          break;
        }
      }
      if (skipFile) continue;
    }
  };

  const removeDocument = (docId: string) => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/documents/${docId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(() => {
        setDocuments(prev => prev.filter(doc => doc.id !== docId));
        setSelectedDocumentIds(prev => prev.filter(id => id !== docId));
      })
      .catch(console.error);
  };

  const updateDocument = (docId: string, updates: Partial<PolicyDocument>) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, ...updates } : doc
    ));
  };

  const getCompletedDocuments = () => {
    return documents.filter(doc => doc.status === 'completed');
  };

  const getPolicyOptions = () => {
    return documents
      .filter(doc => doc.policyOption && doc.status === 'completed')
      .map(doc => doc.policyOption!)
      .filter(Boolean);
  };

  const createChatSession = async (customerId: string | null, projectId: string | null, title?: string): Promise<string | null> => {
    if (!token) {
      console.error('Cannot create chat session: no authentication token');
      return null;
    }
    
    if (!projectId) {
      console.error('Cannot create chat session: projectId is required for project isolation');
      return null;
    }
    
    // STRICT PROJECT ISOLATION: Ensure we're only creating sessions for the current project
    if (projectId !== currentProjectId) {
      console.error('🚨 CreateChatSession: Attempted to create session for different project!');
      console.error('   Current project:', currentProjectId);
      console.error('   Requested project:', projectId);
      return null;
    }
    
    try {
      const defaultTitle = customerId ? 'Project Chat' : 'New Chat';
      const finalTitle = title || defaultTitle;
      console.log('Creating chat session with title:', finalTitle, 'customerId:', customerId, 'projectId:', projectId);
      
      const response = await fetch(`${API_BASE_URL}/api/chat-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: finalTitle,
          customerId: customerId,
          projectId: projectId
        }),
      });
      
      if (response.ok) {
        const session = await response.json();
        
        // STRICT PROJECT ISOLATION: Verify the created session belongs to current project
        if (session.projectId !== currentProjectId) {
          console.error('🚨 CreateChatSession: Created session does not belong to current project!');
          console.error('   Expected project:', currentProjectId);
          console.error('   Session project:', session.projectId);
          return null;
        }
        
        console.log('Created chat session:', session);
        return session.id;
      } else {
        console.error('Failed to create chat session, response:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to create chat session:', error);
    }
    return null;
  };

  const addChatMessage = async (message: ChatMessage, title?: string, explicitSessionId?: string): Promise<string | null> => {
    console.log('🔄 addChatMessage called with title:', title, 'currentCustomerId:', currentCustomerId, 'currentProjectId:', currentProjectId, 'currentChatSessionId:', currentChatSessionId);
    setChatHistory(prev => prev.some(msg => msg.id === message.id) ? prev : [...prev, message]);

    let sessionId: string | null = explicitSessionId ?? currentChatSessionId;

    if (token && currentProjectId) {
      try {
        // Determine or create a chat session
        console.log('📋 Session ID before:', sessionId);
        if (!sessionId) {
          console.log('🆕 No current session, creating new session with title:', title);
          sessionId = await createChatSession(currentCustomerId, currentProjectId, title);
          if (sessionId) {
            setCurrentChatSessionId(sessionId);
            console.log('✅ Created and set new session ID:', sessionId);
          }
        } else {
          console.log('📎 Using provided or existing session ID:', sessionId);
        }

        if (sessionId) {
          console.log('💾 Saving message to session:', sessionId);
          await fetch(`${API_BASE_URL}/api/chat-sessions/${sessionId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              content: message.content,
              sender: message.sender,
              context: {
                policyType: selectedPolicyType,
                documentCount: documents.length,
                documentNames: documents.map(doc => doc.name),
                projectId: currentProjectId,
              },
            }),
          });
          console.log('✅ Message saved successfully to session:', sessionId);
        } else {
          console.error('❌ No session ID available to save message');
        }
      } catch (error) {
        console.error('❌ Failed to save chat message:', error);
      }
    }
    return sessionId || null;
  };

  const clearChatHistory = async (): Promise<void> => {
    setChatHistory([]);

    if (token) {
      try {
        await fetch(`${API_BASE_URL}/api/chat/history`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Failed to clear chat history on server:', error);
      }
    }
  };

  const exportChatHistory = () => {
    try {
      const content = `RISKNINJA CHAT CONVERSATION EXPORT
Generated: ${new Date().toLocaleString()}
User: ${user?.firstName} ${user?.lastName} (${user?.email})
Policy Type: ${selectedPolicyType}
Documents: ${documents.length} uploaded
Messages: ${chatHistory.length}

${'='.repeat(60)}

${chatHistory.map(msg => {
  const time = new Date(msg.timestamp).toLocaleTimeString();
  const sender = msg.sender.toUpperCase();
  return `[${time}] ${sender}:\n${msg.content}\n`;
}).join('\n')}

${'='.repeat(60)}
Export completed by Risk Ninja AI
`;

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `riskninja-chat-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ Chat history exported successfully');
    } catch (error) {
      console.error('❌ Chat export failed:', error);
      alert('Failed to export chat history. Please try again.');
    }
  };

  const setCurrentCustomer = (customerId: string | null) => {
    console.log('🔄 setCurrentCustomer called with:', customerId, 'current:', currentCustomerId);
    // Only clear session if we're switching to a different customer
    if (currentCustomerId !== customerId) {
      console.log('🔄 Switching customer, clearing session and project');
      setCurrentCustomerId(customerId);
      // Clear current project, chat session, and data when switching customers
      setCurrentProjectId(null);
      setCurrentChatSessionId(null);
      setChatHistory([]);
      setDocuments([]);
    } else {
      console.log('📋 Same customer, keeping current session');
    }
  };

  const setCurrentProject = (projectId: string | null) => {
    console.log('🔄 setCurrentProject called with:', projectId, 'current:', currentProjectId);
    console.log('🔄 Stack trace:', new Error().stack);
    
    // AGGRESSIVE CLEARING: Always clear data when switching projects, even if it's the "same" project
    // This prevents any potential race conditions or stale data issues
    console.log('🔄 Clearing all project data before switching');
    setCurrentChatSessionId(null);
    setChatHistory([]);
    setDocuments([]);
    setSelectedDocumentIds([]);
    
    // Update the project ID
    setCurrentProjectId(projectId);
    
    if (currentProjectId !== projectId) {
      console.log('🔄 Switching to different project:', currentProjectId, '→', projectId);
    } else {
      console.log('🔄 Re-selecting same project, data cleared for fresh load');
    }
  };

  const setCurrentChatSession = (sessionId: string | null) => {
    console.log('🔄 setCurrentChatSession called with:', sessionId, 'previous:', currentChatSessionId);
    setCurrentChatSessionId(sessionId);
  };

  const startNewSession = () => {
    setChatHistory([]);
    clearDocuments();
    setCurrentChatSessionId(null);
  };

  const value: DocumentContextType = {
    documents,
    createChatSession,
    selectedDocumentIds,
    selectedDocuments,
    selectedPolicyType,
    chatHistory,
    currentCustomerId,
    currentProjectId,
    currentChatSessionId,
    setDocuments,
    setSelectedDocumentIds,
    toggleDocumentSelection,
    addDocuments,
    removeDocument,
    updateDocument,
    setSelectedPolicyType,
    setChatHistory,
    addChatMessage,
    clearChatHistory,
    exportChatHistory,
    getCompletedDocuments,
    getPolicyOptions,
    clearDocuments,
    startNewSession,
    setCurrentCustomer,
    setCurrentProject,
    setCurrentChatSession,
    refreshDocuments
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
} 